import { Autocomplete, TextField, type TextFieldProps } from '@mui/material'
import type { ClipboardEvent, KeyboardEvent, ReactNode } from 'react'
import { useEffect, useState } from 'react'

type TextFieldRestProps = Omit<
  TextFieldProps,
  'value' | 'onChange' | 'placeholder' | 'error' | 'helperText' | 'fullWidth'
>

type EmailInputProps = TextFieldRestProps & {
  value?: string[]
  placeholder?: string
  error?: ReactNode
  fullWidth?: boolean
  onChange?: (emails: string[]) => void
}

function EmailInput({
  error,
  fullWidth = true,
  onChange,
  placeholder,
  value,
  ...rest
}: EmailInputProps) {
  const [emails, setEmails] = useState<string[]>(value ?? [])
  const [inputValue, setInputValue] = useState<string>('')

  useEffect(() => {
    setEmails(value ?? [])
  }, [value])

  const handleChange = (next: string[]) => {
    const unique = normalizeEmails(next)
    setEmails(unique)
    if (onChange) onChange(unique)
  }

  const addFromInput = (text?: string) => {
    const toAdd = splitEmails(text ?? inputValue)
    if (!toAdd.length) return

    handleChange([...emails, ...toAdd])
    setInputValue('')
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (['Enter', ',', ';', ' '].includes(event.key)) {
      event.preventDefault()
      addFromInput()
    }

    if (['Tab'].includes(event.key)) {
      addFromInput()
    }
  }

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pastedText = event.clipboardData?.getData('text')
    if (!pastedText) return

    event.preventDefault()
    addFromInput(pastedText)
  }

  return (
    <Autocomplete
      multiple
      freeSolo
      options={[]}
      value={emails}
      onChange={(_, newValue) => handleChange(newValue)}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      filterSelectedOptions
      renderInput={params => (
        <TextField
          {...params}
          {...rest}
          fullWidth={fullWidth}
          placeholder={placeholder}
          error={Boolean(error)}
          helperText={error}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
        />
      )}
    />
  )
}

export default EmailInput

function splitEmails(text: string): string[] {
  return text
    .split(/[;, ]+/)
    .map(email => email.trim())
    .filter(Boolean)
}

function normalizeEmails(list: string[]) {
  return [...new Set(list.map(email => email.trim()).filter(Boolean))]
}
